import {
  Box,
  Flex,
  Heading,
  HStack,
  SegmentGroup,
  SimpleGrid,
  Skeleton,
  Text,
  VStack
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useUserDetails } from "../../hooks/useAuthQueries"
import { useMemo, useState } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts"
import {
  getFinancials,
  getTenantPower
} from "../../services/portfolioApi"
import SummaryStatCard from "./SummaryStatCard"
import { getNetHeatPumpCost } from "../../utils/financials"

interface FinancialsTabProps {
  homeId: number
}

const CostLegend = ({ payload }: any) => {
  const formatLabel = (value: string) => {
    return value === "currentCost" ? "Current cost" : "Cost after upgrades"
  }
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "24px",
        paddingTop: "20px"
      }}
    >
      {payload?.map((entry: any, index: number) => (
        <div
          key={index}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: entry.color,
              borderRadius: "2px"
            }}
          />
          <span style={{ fontSize: "12px", color: "#4a5568" }}>
            {formatLabel(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

const KwhLegend = ({ payload }: any) => {
  const formatLabel = (value: string) => {
    if (value === "demandKwh") return "Demand after upgrades (kWh)"
    if (value === "generationKwh") return "Generation (kWh)"
    if (value === "exportKwh") return "Export (kWh)"
    return value
  }
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "24px",
        paddingTop: "20px"
      }}
    >
      {payload?.map((entry: any, index: number) => (
        <div
          key={index}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: entry.color,
              borderRadius: "2px"
            }}
          />
          <span style={{ fontSize: "12px", color: "#4a5568" }}>
            {formatLabel(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

const FinancialsTab = ({ homeId }: FinancialsTabProps) => {
  const { data: userDetails } = useUserDetails()
  const isPropertyInvestor = userDetails?.data?.organisation?.organisationType === "property_investment"

  const {
    data: financials,
    isLoading,
    error
  } = useQuery({
    queryKey: ["financials", homeId],
    queryFn: () => getFinancials(homeId),
    enabled: !!localStorage.getItem("token") && homeId > 0
  })

  const { data: tenantPower, isLoading: isLoadingTenantPower } = useQuery({
    queryKey: ["tenantPower", homeId],
    queryFn: () => getTenantPower(homeId),
    enabled: !!localStorage.getItem("token") && homeId > 0 && !!userDetails && !isPropertyInvestor,
    retry: false,
  })

  const [chartUnit, setChartUnit] = useState<"£" | "kWh">("£")

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ]

  const electricityCostChartData = useMemo(() => {
    if (!financials?.monthly) return []
    return financials.monthly.map((month) => {
      const currentCost = month.currentDemandTotalCost
      const hasExports = month.generationKwh >= month.demandAfterUpgradesKwh
      const newCost = month.finalCostAfterSavings

      return {
        month: monthNames[month.month - 1],
        currentCost: Math.round(currentCost * 100) / 100,
        newCost: Math.round(newCost * 100) / 100,
        hasExports
      }
    })
  }, [financials])

  const energyKwhChartData = useMemo(() => {
    if (!financials?.monthly) return []
    return financials.monthly.map((month) => ({
      month: monthNames[month.month - 1],
      demandKwh: Math.round(month.demandAfterUpgradesKwh * 10) / 10,
      generationKwh: Math.round(month.generationKwh * 10) / 10,
      exportKwh: Math.round(Math.abs(month.exportKwh) * 10) / 10
    }))
  }, [financials])

  const { kwhYAxisDomain, kwhYAxisTicks } = useMemo(() => {
    if (!energyKwhChartData.length)
      return {
        kwhYAxisDomain: [0, 500],
        kwhYAxisTicks: [0, 100, 200, 300, 400, 500]
      }
    const maxValue = Math.max(
      ...energyKwhChartData.map((d) =>
        Math.max(d.demandKwh, d.generationKwh, d.exportKwh)
      )
    )

    const rough = maxValue * 1.1
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)))
    const residual = rough / magnitude
    const step =
      residual <= 2 ? magnitude / 2 : residual <= 5 ? magnitude : magnitude * 2
    const roundedMax = Math.ceil(rough / step) * step

    const ticks: number[] = []
    for (let i = 0; i <= roundedMax; i += step) {
      ticks.push(Math.round(i))
    }

    return { kwhYAxisDomain: [0, roundedMax], kwhYAxisTicks: ticks }
  }, [energyKwhChartData])

  const { costYAxisDomain, costYAxisTicks } = useMemo(() => {
    if (!electricityCostChartData.length)
      return { costYAxisDomain: [-100, 100], costYAxisTicks: [-100, 0, 100] }
    const maxValue = Math.max(
      ...electricityCostChartData.map((d) => Math.max(d.currentCost, d.newCost))
    )
    const minValue = Math.min(
      ...electricityCostChartData.map((d) => Math.min(d.currentCost, d.newCost))
    )
    const roundedMin = Math.min(
      -100,
      minValue < 0 ? Math.floor(minValue / 100) * 100 : -100
    )
    const roundedMax = Math.ceil((maxValue * 1.1) / 100) * 100

    const ticks: number[] = []
    for (let i = roundedMin; i <= roundedMax; i += 100) {
      ticks.push(i)
    }

    return { costYAxisDomain: [roundedMin, roundedMax], costYAxisTicks: ticks }
  }, [electricityCostChartData])

  if (error) {
    return (
      <Box bg="white" p={6} borderRadius="lg">
        <Text color="red.500">Error loading cost summary</Text>
      </Box>
    )
  }

  if (isLoading || !financials) {
    return (
      <VStack gap={6} align="stretch" overflowX="hidden" maxW="100%" pb={20}>
        <Box
          bg="white"
          p={6}
          borderRadius="lg"
          minH="75vh"
          overflowX="hidden"
          maxW="100%"
        >
          <VStack gap={6} align="stretch" overflowX="hidden">
            <Heading size="lg">Energy Costs Comparison</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              {[0, 1, 2].map((index) => (
                <Box
                  key={`summary-loading-${index}`}
                  p={5}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  <Skeleton height="14px" width="60%" mb={3} />
                  <Skeleton height="36px" width="70%" mb={2} />
                  <Skeleton height="10px" width="50%" />
                </Box>
              ))}
            </SimpleGrid>

            <VStack gap={4} align="stretch">
              {[0, 1].map((index) => (
                <Box
                  key={`comparison-loading-${index}`}
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  p={3}
                  bg="white"
                >
                  <SimpleGrid
                    columns={{ base: 1, md: 4 }}
                    gap={{ base: 2, md: 4 }}
                    alignItems="center"
                  >
                    <Skeleton height="20px" width="120px" />
                    <Skeleton height="18px" width="80%" />
                    <Skeleton height="18px" width="80%" />
                    <Skeleton height="16px" width="60%" />
                  </SimpleGrid>
                </Box>
              ))}
            </VStack>
          </VStack>
        </Box>
      </VStack>
    )
  }

  const currentGasCosts = financials.currentGasCost
  const currentElectricityCosts = financials.currentElectricityCost
  const totalCurrentCosts = financials.currentEnergyCost
  const gasCostsAfterUpgrades = financials.gasCostAfterUpgrades
  const electricityCostsAfterUpgrades = financials.electricityCostAfterUpgrades
  const totalCostsAfterUpgrades = financials.totalCostAfterUpgrades
  const totalSaving = financials.annualSavings
  const savingPercentage = totalCurrentCosts
    ? (totalSaving / totalCurrentCosts) * 100
    : 0

  const formatCurrency = (value: number) =>
    `£${Math.round(value).toLocaleString()}`
  const comparisonRows = [
    {
      key: "gas",
      label: "Gas Costs",
      icon: "🔥",
      current: currentGasCosts,
      after: gasCostsAfterUpgrades
    },
    {
      key: "electricity",
      label: "Electricity Costs",
      icon: "⚡",
      current: currentElectricityCosts,
      after: electricityCostsAfterUpgrades
    }
  ]

  return (
    <VStack gap={6} align="stretch" overflowX="hidden" maxW="100%" pb={20}>
      <Box
        bg="white"
        p={6}
        borderRadius="lg"
        minH="75vh"
        overflowX="hidden"
        maxW="100%"
      >
        <VStack gap={6} align="stretch" overflowX="hidden">
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} overflowX="hidden">
            {(() => {
              const solarPvCost = financials.solarPvCost || 0
              const batteryCost = financials.batteryCost || 0
              const netHeatPumpCost = getNetHeatPumpCost(
                financials.heatPumpCost,
                financials.heatPumpGrant
              )
              const totalUpgradeCosts =
                solarPvCost + batteryCost + netHeatPumpCost
              const paybackPeriod = financials.paybackPeriod
              const roi = financials.roi

              return (
                <>
                  <SummaryStatCard
                    title="Total upgrade costs"
                    value={formatCurrency(totalUpgradeCosts)}
                    subline="Solar + Battery + Heat pump"
                    borderColor="gray.100"
                  />
                  <SummaryStatCard
                    title="Payback period"
                    value={
                      paybackPeriod != null
                        ? `${paybackPeriod} years`
                        : "N/A"
                    }
                    subline="Time to recover investment"
                    borderColor="gray.100"
                  />
                  <SummaryStatCard
                    title="Retrofit ROI %"
                    value={roi != null ? `${roi.toFixed(1)}%` : "0%"}
                    subline="Annual return on investment"
                    borderColor="gray.100"
                  />
                </>
              )
            })()}
          </SimpleGrid>

          <Heading size="lg">Upgrades Costs</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} overflowX="hidden">
            <SummaryStatCard
              title={`Solar PV${
                financials.solarKw
                  ? ` (${financials.solarKw} kW)`
                  : ""
              }`}
              value={
                financials.solarPvCost
                  ? formatCurrency(financials.solarPvCost)
                  : formatCurrency(0)
              }
              subline={
                financials.solarPanels && financials.solarKw
                  ? `${financials.solarPanels} x ${Math.round((financials.solarKw / financials.solarPanels) * 1000)}W panels`
                  : undefined
              }
              bgColor="gray.100"
            />
            <SummaryStatCard
              title={`Battery${
                financials.batteryKw
                  ? ` (${financials.batteryKw} kW)`
                  : ""
              }`}
              value={
                typeof financials.batteryCost === "number"
                  ? formatCurrency(financials.batteryCost)
                  : formatCurrency(0)
              }
              subline="battery installed"
              bgColor="gray.100"
            />
            <SummaryStatCard
              title={`Air Source Heat Pump${
                financials.heatPumpKw
                  ? ` (${financials.heatPumpKw} kW)`
                  : ""
              }`}
              value={formatCurrency(
                getNetHeatPumpCost(
                  financials.heatPumpCost,
                  financials.heatPumpGrant
                )
              )}
              subline={`Net spending after ${formatCurrency(financials.heatPumpGrant)} grant`}
              bgColor="gray.100"
            />
          </SimpleGrid>

          <Heading size="lg">Energy Costs Comparison</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} overflowX="hidden">
            <SummaryStatCard
              title="Current total"
              value={formatCurrency(totalCurrentCosts)}
              subline="per year today"
              bgColor="gray.50"
            />
            <SummaryStatCard
              title="After upgrades"
              value={formatCurrency(totalCostsAfterUpgrades)}
              subline="includes solar gains"
              valueColor="green.600"
              sublineColor="gray.500"
              borderColor="gray.100"
            />
            <SummaryStatCard
              title="Annual savings"
              value={formatCurrency(totalSaving)}
              subline={`${Math.round(savingPercentage)}% reduction vs today`}
              titleColor="green.700"
              valueColor="green.600"
              sublineColor="green.600"
              bgColor="green.50"
              borderColor="green.200"
            />
          </SimpleGrid>

          <VStack gap={4} align="stretch">
            {comparisonRows.map((row) => {
              const diff = row.after - row.current
              const isSaving = diff < 0
              const diffLabel = isSaving
                ? `${formatCurrency(Math.abs(diff))} saved`
                : `${formatCurrency(diff)} extra`

              return (
                <Box
                  key={row.key}
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  p={3}
                  bg="white"
                >
                  <SimpleGrid
                    columns={{ base: 1, md: 4 }}
                    gap={{ base: 2, md: 4 }}
                    alignItems="center"
                    overflowX="hidden"
                  >
                    <Flex align="center" gap={2} minW={0}>
                      {row.icon && (
                        <Text
                          fontSize="lg"
                          role="img"
                          aria-hidden="true"
                          flexShrink={0}
                        >
                          {row.icon}
                        </Text>
                      )}
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.700"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {row.label}
                      </Text>
                    </Flex>
                    <Box minW={0}>
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Current
                      </Text>
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {formatCurrency(row.current)}
                      </Text>
                    </Box>
                    <Box minW={0}>
                      <Text
                        fontSize="xs"
                        color="green.700"
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        After upgrades
                      </Text>
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        color="green.700"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {formatCurrency(row.after)}
                      </Text>
                    </Box>
                    <Flex
                      align="center"
                      justify={{ base: "flex-start", md: "flex-end" }}
                      minW={0}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color={isSaving ? "green.600" : "orange.600"}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {diffLabel}
                      </Text>
                    </Flex>
                  </SimpleGrid>
                </Box>
              )
            })}
          </VStack>

          {/* Electricity Cost Comparison Chart */}
          {isLoading ? (
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
              bg="white"
            >
              <Skeleton height="300px" />
            </Box>
          ) : financials?.monthly && electricityCostChartData.length > 0 ? (
            <>
              <HStack
                justify="space-between"
                align="center"
                flexWrap="wrap"
                gap={3}
              >
                <Heading size="lg">Monthly Electricity Cost Comparison</Heading>
                <SegmentGroup.Root
                  value={chartUnit}
                  onValueChange={(e) => setChartUnit(e.value as "£" | "kWh")}
                  display="inline-flex"
                  width="fit-content"
                  bg="gray.100"
                  borderRadius="md"
                  p="1"
                  boxShadow="sm"
                  gap="1"
                >
                  <SegmentGroup.Indicator
                    bg="white"
                    borderRadius="md"
                    boxShadow="sm"
                  />
                  <SegmentGroup.Item value="£" px={4} py={2}>
                    <SegmentGroup.ItemText>£</SegmentGroup.ItemText>
                    <SegmentGroup.ItemHiddenInput />
                  </SegmentGroup.Item>
                  <SegmentGroup.Item value="kWh" px={4} py={2}>
                    <SegmentGroup.ItemText>kWh</SegmentGroup.ItemText>
                    <SegmentGroup.ItemHiddenInput />
                  </SegmentGroup.Item>
                </SegmentGroup.Root>
              </HStack>
              <Box
                border="1px solid"
                borderColor="gray.100"
                borderRadius="md"
                p={4}
                bg="white"
              >
                {chartUnit === "£" ? (
                  <>
                    <Box width="100%" height="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={electricityCostChartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e2e8f0"
                          />
                          <ReferenceLine
                            y={0}
                            stroke="#2d3748"
                            strokeWidth={1.5}
                          />
                          <XAxis
                            dataKey="month"
                            tick={{ fill: "#4a5568", fontSize: 12 }}
                            stroke="#cbd5e0"
                          />
                          <YAxis
                            type="number"
                            domain={[costYAxisDomain[0], costYAxisDomain[1]]}
                            ticks={costYAxisTicks}
                            interval={0}
                            tick={{ fill: "#4a5568", fontSize: 12 }}
                            stroke="#cbd5e0"
                            width={60}
                            allowDataOverflow={true}
                            tickFormatter={(value) => `£${value}`}
                          />
                          <Tooltip
                            content={({ active, payload }: any) => {
                              if (!active || !payload || !payload.length)
                                return null
                              const data = payload[0].payload
                              const savings = data.currentCost - data.newCost
                              return (
                                <Box
                                  bg="white"
                                  border="1px solid #e2e8f0"
                                  borderRadius="6px"
                                  p="8px 12px"
                                  boxShadow="md"
                                >
                                  <Text fontWeight="bold" mb="4px">
                                    {data.month}
                                  </Text>
                                  <Text color="#718096">
                                    Current Energy Cost: £
                                    {data.currentCost.toFixed(2)}
                                  </Text>
                                  <Text color="#38a169">
                                    Energy Cost after upgrades: £
                                    {data.newCost.toFixed(2)}
                                  </Text>
                                  <Text
                                    color="#3182ce"
                                    fontWeight="medium"
                                    mt="4px"
                                  >
                                    {data.hasExports
                                      ? "Savings & Exports"
                                      : "Savings"}
                                    : £{savings.toFixed(2)}
                                  </Text>
                                </Box>
                              )
                            }}
                          />
                          <Legend content={<CostLegend />} />
                          <Bar
                            dataKey="currentCost"
                            fill="#718096"
                            radius={[4, 4, 0, 0]}
                            name="currentCost"
                          />
                          <Bar
                            dataKey="newCost"
                            fill="#38a169"
                            radius={[4, 4, 0, 0]}
                            name="newCost"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box width="100%" height="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={energyKwhChartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e2e8f0"
                          />
                          <ReferenceLine
                            y={0}
                            stroke="#2d3748"
                            strokeWidth={1.5}
                          />
                          <XAxis
                            dataKey="month"
                            tick={{ fill: "#4a5568", fontSize: 12 }}
                            stroke="#cbd5e0"
                          />
                          <YAxis
                            type="number"
                            domain={[kwhYAxisDomain[0], kwhYAxisDomain[1]]}
                            ticks={kwhYAxisTicks}
                            interval={0}
                            tick={{ fill: "#4a5568", fontSize: 12 }}
                            stroke="#cbd5e0"
                            width={60}
                            allowDataOverflow={true}
                            tickFormatter={(value) => `${value}`}
                          />
                          <Tooltip
                            content={({ active, payload }: any) => {
                              if (!active || !payload || !payload.length)
                                return null
                              const data = payload[0].payload
                              return (
                                <Box
                                  bg="white"
                                  border="1px solid #e2e8f0"
                                  borderRadius="6px"
                                  p="8px 12px"
                                  boxShadow="md"
                                >
                                  <Text fontWeight="bold" mb="4px">
                                    {data.month}
                                  </Text>
                                  <Text color="#718096">
                                    Demand: {data.demandKwh.toFixed(1)} kWh
                                  </Text>
                                  <Text color="#fdbb58">
                                    Generation: {data.generationKwh.toFixed(1)}{" "}
                                    kWh
                                  </Text>
                                  <Text color="#A144A4">
                                    Export: {data.exportKwh.toFixed(1)} kWh
                                  </Text>
                                </Box>
                              )
                            }}
                          />
                          <Legend content={<KwhLegend />} />
                          <Bar
                            dataKey="demandKwh"
                            fill="#718096"
                            radius={[4, 4, 0, 0]}
                            name="demandKwh"
                          />
                          <Bar
                            dataKey="generationKwh"
                            fill="#fdbb58"
                            radius={[4, 4, 0, 0]}
                            name="generationKwh"
                          />
                          <Bar
                            dataKey="exportKwh"
                            fill="#A144A4"
                            radius={[4, 4, 0, 0]}
                            name="exportKwh"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </>
                )}
              </Box>
            </>
          ) : null}

          {/* Tenant Power Section */}
          {!isPropertyInvestor && (isLoadingTenantPower ? (
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
              bg="gray.50"
            >
              <Skeleton height="200px" />
            </Box>
          ) : tenantPower?.eligible ? (
            <VStack gap={4} align="stretch">
              <Heading size="lg">Octopus Tenant Power</Heading>

              <Heading size="md" color="gray.600">Tenant Benefit</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} overflowX="hidden">
                <SummaryStatCard
                  title="Tenant annual saving"
                  value={formatCurrency(tenantPower.tenant.annualSaving)}
                  subline={`30% off ${Math.round(tenantPower.tenant.electricityConsumptionKwh).toLocaleString()} kWh electricity`}
                  bgColor="gray.100"
                />
                <SummaryStatCard
                  title="Tenant monthly saving"
                  value={formatCurrency(tenantPower.tenant.monthlySaving)}
                  subline="Estimated monthly benefit"
                  bgColor="gray.100"
                />
                <SummaryStatCard
                  title="Tariff discount"
                  value={`${Math.round(tenantPower.tenant.discountRate * 100)}%`}
                  subline="Off electricity consumption"
                  bgColor="gray.100"
                />
              </SimpleGrid>

              <Heading size="md" color="gray.600">Investor Recovery</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} overflowX="hidden">
                <SummaryStatCard
                  title={`Solar (${tenantPower.investor.solarPanels} panels)`}
                  value={formatCurrency(tenantPower.investor.solarAnnualPayment)}
                  subline={`${Math.round(tenantPower.investor.kwhPerPanel)} kWh/panel · ${Math.round(tenantPower.investor.generationRatio * 100)}% of benchmark`}
                  bgColor="gray.100"
                />
                <SummaryStatCard
                  title={`Battery (${tenantPower.investor.batteryKw} kWh)`}
                  value={formatCurrency(tenantPower.investor.batteryAnnualPayment)}
                  subline="Annual asset payment"
                  bgColor="gray.100"
                />
                <SummaryStatCard
                  title="Total annual recovery"
                  value={formatCurrency(tenantPower.investor.totalAnnualPayment)}
                  subline="Solar + battery combined"
                  titleColor="green.700"
                  valueColor="green.600"
                  sublineColor="green.600"
                  bgColor="green.50"
                  borderColor="green.200"
                />
              </SimpleGrid>

              {/* Landlord investment case with Tenant Power */}
              {(() => {
                const solarPvCost = financials.solarPvCost || 0
                const batteryCost = financials.batteryCost || 0
                const fallbackNetCost =
                  solarPvCost +
                  batteryCost +
                  getNetHeatPumpCost(
                    financials.heatPumpCost,
                    financials.heatPumpGrant
                  )
                const netCost =
                  tenantPower.investor.netUpgradeCost ??
                  fallbackNetCost
                const tpPayment = tenantPower.investor.totalAnnualPayment
                const annualReturn = tenantPower.investor.annualReturn ?? tpPayment
                if (netCost <= 0 || annualReturn <= 0) return null
                const paybackWithTp = tenantPower.investor.paybackPeriod ?? netCost / annualReturn
                const roiWithTp = tenantPower.investor.roi ?? (annualReturn / netCost) * 100

                return (
                  <>
                    <Heading size="md" color="gray.600">Landlord Investment Case</Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} overflowX="hidden">
                      <SummaryStatCard
                        title="Payback with Tenant Power"
                        value={`${paybackWithTp.toFixed(1)} years`}
                        subline="Full upgrade cost / Octopus payments"
                        valueColor="#5840FF"
                        sublineColor="gray.500"
                        bgColor="gray.100"
                      />
                      <SummaryStatCard
                        title="Annual landlord payment"
                        value={formatCurrency(annualReturn)}
                        subline="Paid by Octopus; tenant savings excluded"
                        bgColor="gray.100"
                      />
                      <SummaryStatCard
                        title="ROI with Tenant Power"
                        value={`${roiWithTp.toFixed(1)}%`}
                        subline="Tenant bill savings are excluded"
                        valueColor="#5840FF"
                        sublineColor="gray.500"
                        bgColor="gray.100"
                      />
                    </SimpleGrid>
                  </>
                )
              })()}

              <Box
                bg="orange.50"
                border="1px solid"
                borderColor="orange.200"
                borderRadius="md"
                p={4}
              >
                <Text fontSize="sm" fontWeight="semibold" color="orange.800" mb={2}>
                  Important caveats
                </Text>
                <VStack gap={1} align="stretch">
                  {tenantPower.caveats.map((caveat, i) => (
                    <Text key={i} fontSize="sm" color="orange.900">
                      · {caveat}
                    </Text>
                  ))}
                </VStack>
              </Box>
            </VStack>
          ) : null)}
        </VStack>
      </Box>
    </VStack>
  )
}

export default FinancialsTab
